/**
 * Tarih yönetimi hook'u
 * Departure date ve yacht tarih aralığı yönetimi
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { addDays } from 'date-fns'
import { DEFAULT_INITIAL_DEPARTURE_DAYS_AHEAD, DEFAULT_YACHT_CHECK_IN_DAYS_AHEAD, DEFAULT_YACHT_RENTAL_DAYS } from '@/constants/pricing'
import { getSearchFilters } from '@/lib/cookies'
import { toLocalDateString, parseLocalDateString } from '@/lib/date-utils'
import type { TravelersPreferences } from '@/types/travelers'

export interface UseDateManagementReturn {
    departureDate: Date | undefined
    yachtCheckInDate: Date | null
    yachtCheckOutDate: Date | null
    setDepartureDate: (date: Date | undefined) => void
    setYachtCheckInDate: (date: Date | null) => void
    setYachtCheckOutDate: (date: Date | null) => void
    updateFormDates: (updateFormValue: (key: string, value: any) => void) => void
}

export function useDateManagement(
    preferences: TravelersPreferences | null,
    activeProductType: string,
    setContextDepartureDate: (date: Date | null) => void
): UseDateManagementReturn {
    const [departureDate, setDepartureDate] = useState<Date | undefined>(
        preferences?.departure_date ? parseLocalDateString(preferences.departure_date) ?? undefined : undefined
    )
    const [yachtCheckInDate, setYachtCheckInDate] = useState<Date | null>(null)
    const [yachtCheckOutDate, setYachtCheckOutDate] = useState<Date | null>(null)
    
    const dateInitializedRef = useRef<boolean>(false)
    const yachtDateInitializedRef = useRef<boolean>(false)
    
    // Departure date: context (çerez) yüklendikten sonra preferences.departure_date forma yansıtılır
    // preferences null iken (ana sayfa ilk açılış) varsayılan tarih göster ki input placeholder değil değer göstersin
    useEffect(() => {
        if (dateInitializedRef.current) return
        if (preferences === null) {
            if (typeof window !== 'undefined') {
                setDepartureDate((prev) => (prev !== undefined ? prev : addDays(new Date(), DEFAULT_INITIAL_DEPARTURE_DAYS_AHEAD)))
            }
            return
        }
        if (preferences?.departure_date) {
            const date = parseLocalDateString(preferences.departure_date)
            if (date) setDepartureDate(date)
        }
        dateInitializedRef.current = true
    }, [preferences, preferences?.departure_date])
    
    // Yacht tarih aralığı initialization (sadece yacht tab'ı aktifse ve bir kez)
    // NOT: Varsayılan tarih eklenmez - kullanıcı manuel seçmeli
    useEffect(() => {
        if (activeProductType !== 'yacht' || yachtDateInitializedRef.current) return
        
        const searchFilters = getSearchFilters()
        const cookieCheckIn = searchFilters?.check_in_date
        const cookieCheckOut = searchFilters?.check_out_date
        
        if (cookieCheckIn) {
            const prefDate = parseLocalDateString(cookieCheckIn)
            if (prefDate) {
                setYachtCheckInDate(prefDate)
                if (cookieCheckOut) {
                    const prefCheckOut = parseLocalDateString(cookieCheckOut)
                    if (prefCheckOut) {
                        setYachtCheckOutDate(prefCheckOut)
                    }
                }
                yachtDateInitializedRef.current = true
                return
            }
        }
        
        // Varsayılan tarih eklenmez - boş başlat
        yachtDateInitializedRef.current = true
    }, [activeProductType])
    
    // Yacht product type değiştiğinde ref'i sıfırla
    useEffect(() => {
        if (activeProductType !== 'yacht') {
            yachtDateInitializedRef.current = false
        }
    }, [activeProductType])
    
    // Form değerlerini güncelleme helper'ı - sadece tarih değiştiğinde çağrılmalı
    const updateFormDates = useCallback((updateFormValue: (key: string, value: any) => void) => {
        if (activeProductType === 'yacht') {
            if (yachtCheckInDate) {
                const checkInStr = toLocalDateString(yachtCheckInDate)
                updateFormValue('check_in_date', checkInStr)
                updateFormValue('departure_date', checkInStr)
            }
            if (yachtCheckOutDate) {
                updateFormValue('check_out_date', toLocalDateString(yachtCheckOutDate))
            }
        } else if (departureDate) {
            const dateStr = toLocalDateString(departureDate)
            updateFormValue('departure_date', dateStr)
            updateFormValue('check_in_date', dateStr)
        }
    }, [activeProductType, departureDate, yachtCheckInDate, yachtCheckOutDate])
    
    return {
        departureDate,
        yachtCheckInDate,
        yachtCheckOutDate,
        setDepartureDate,
        setYachtCheckInDate,
        setYachtCheckOutDate,
        updateFormDates,
    }
}

